import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { isOidcAdminAuth } from '../lib/oidc/oidcConfig';
import { ADMIN_OIDC_TOKEN_EVENT, getOidcAccessToken, startOidcAuthorize } from '../lib/oidc/oidcSession';

function subscribe(onChange: () => void): () => void {
  window.addEventListener(ADMIN_OIDC_TOKEN_EVENT, onChange);
  return () => window.removeEventListener(ADMIN_OIDC_TOKEN_EVENT, onChange);
}

/** The in-memory OIDC access token, re-rendering on login / refresh / logout. */
export function useOidcAccessToken(): string | null {
  return useSyncExternalStore(subscribe, getOidcAccessToken, () => null);
}

export function AuthStatusCard({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper elevation={0} sx={{ maxWidth: 440, width: 1, p: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {children}
      </Paper>
    </Box>
  );
}

export function AuthSpinner({ text }: { text: string }) {
  return (
    <AuthStatusCard>
      <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
        <CircularProgress size={40} thickness={3} />
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Stack>
    </AuthStatusCard>
  );
}

/**
 * RBAC Phase 3 — protected routes in OIDC mode render only with an access token in memory.
 * First render without one (a reload / new tab) runs a top-level `prompt=none` authorize; the
 * callback falls back to an interactive authorize when auth-server says `login_required`.
 * Password mode renders the routes unchanged (Layout / shells keep their own probes).
 */
export function OidcSessionGate() {
  if (!isOidcAdminAuth()) return <Outlet />;
  return <OidcSessionGateInner />;
}

function OidcSessionGateInner() {
  const token = useOidcAccessToken();
  const hadTokenAtMount = useRef(token !== null);
  const started = useRef(false);

  useEffect(() => {
    if (token || started.current || hadTokenAtMount.current) return;
    started.current = true;
    void startOidcAuthorize({ kind: 'silent' });
  }, [token]);

  if (token) {
    hadTokenAtMount.current = true;
    return <Outlet />;
  }
  if (!hadTokenAtMount.current) return <AuthSpinner text="Signing you in…" />;

  // The session ended while the page was open (refresh token revoked / expired).
  return (
    <AuthStatusCard>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Your session has ended. Sign in again to continue.
      </Alert>
      <Button fullWidth variant="contained" onClick={() => void startOidcAuthorize({ kind: 'interactive' })}>
        Sign in
      </Button>
    </AuthStatusCard>
  );
}
